import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { translate, Language } from '../utils/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    loadLanguage();
  }, [user]);

  const loadLanguage = async () => {
    if (!user) {
      const browserLanguage = navigator.language.toLowerCase();
      if (browserLanguage.startsWith('fr')) {
        setLanguageState('fr');
      } else if (browserLanguage.startsWith('es')) {
        setLanguageState('es');
      } else if (browserLanguage.startsWith('nl')) {
        setLanguageState('nl');
      } else {
        setLanguageState('en');
      }
      return;
    }

    try {
      const { data: settings, error } = await supabase
        .from('user_settings')
        .select('language')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (settings && ['en', 'fr', 'es', 'nl'].includes(settings.language)) {
        setLanguageState(settings.language as Language);
      } else {
        const browserLanguage = navigator.language.toLowerCase();
        if (browserLanguage.startsWith('fr')) {
          setLanguageState('fr');
        } else if (browserLanguage.startsWith('es')) {
          setLanguageState('es');
        } else if (browserLanguage.startsWith('nl')) {
          setLanguageState('nl');
        } else {
          setLanguageState('en');
        }
      }
    } catch (error) {
      console.error('Failed to load language:', error);
    }
  };

  const saveLanguage = async (newLanguage: Language) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          language: newLanguage,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to save language:', error);
    }
  };

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    saveLanguage(newLanguage);
  };

  const t = (key: string): string => {
    return translate(key, language);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
